import { useState, useEffect } from 'react';
import { useStore } from '../state/store.ts';

import { useWsClient } from '../avatar/avatar-canvas.tsx';
import { isValidToken } from '@project-avatar/shared';
import { EFFECT_LABELS, EFFECT_DESCRIPTIONS } from '@project-avatar/avatar-engine';
import type { EffectsState } from '@project-avatar/avatar-engine';
import manifest from '../assets/models/manifest.json';
import type { ModelEntry } from '../types.ts';

const models = (manifest as unknown as { models: ModelEntry[] }).models;

// ─── Styles ────────────────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  zIndex: 200,
  display: 'flex',
  justifyContent: 'flex-start',
};

const drawerStyle: React.CSSProperties = {
  width: '340px',
  maxWidth: '90vw',
  height: '100%',
  background: 'var(--color-surface)',
  borderRight: '1px solid var(--color-border)',
  paddingTop: '1.5rem',
  paddingRight: '1.5rem',
  paddingBottom: '1.5rem',
  paddingLeft: '1.5rem',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '1.25rem',
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(136, 136, 152, 0.3) transparent',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const titleStyle: React.CSSProperties = {
  fontSize: '1.1rem',
  fontWeight: 600,
};

const closeBtnStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 6,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 18,
  color: 'var(--color-text-muted)',
  cursor: 'pointer',
  background: 'transparent',
  border: 'none',
};

const sectionStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
};

const labelStyle: React.CSSProperties = {
  fontSize: '0.8rem',
  fontWeight: 500,
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  fontSize: 13,
  fontFamily: 'var(--font-mono)',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  color: 'var(--color-text)',
  outline: 'none',
};

const btnStyle: React.CSSProperties = {
  padding: '8px 14px',
  fontSize: '0.85rem',
  fontWeight: 500,
  borderRadius: 6,
  cursor: 'pointer',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  cursor: 'pointer',
  appearance: 'auto' as const,
};

const hintStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-muted)',
  lineHeight: 1.4,
};

const tabBarStyle: React.CSSProperties = {
  display: 'flex',
  gap: '0.25rem',
  borderBottom: '1px solid var(--color-border)',
  paddingBottom: 0,
};

const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: '8px 16px',
  fontSize: '0.8rem',
  fontWeight: active ? 600 : 400,
  color: active ? 'var(--color-accent)' : 'var(--color-text-muted)',
  background: 'transparent',
  border: 'none',
  borderBottom: active ? '2px solid var(--color-accent)' : '2px solid transparent',
  cursor: 'pointer',
  transition: 'all 0.15s',
});

const effectToggleStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 0',
  borderBottom: '1px solid rgba(42, 42, 58, 0.5)',
};

const effectToggleSwitchStyle = (on: boolean): React.CSSProperties => ({
  width: 36,
  height: 18,
  borderRadius: 9,
  background: on ? 'var(--color-accent)' : 'var(--color-border)',
  cursor: 'pointer',
  position: 'relative' as const,
  transition: 'background 0.15s',
  flexShrink: 0,
});

const effectToggleKnobStyle = (on: boolean): React.CSSProperties => ({
  width: 14,
  height: 14,
  borderRadius: 7,
  background: '#fff',
  position: 'absolute' as const,
  top: 2,
  left: on ? 20 : 2,
  transition: 'left 0.15s',
});

// ─── Main Drawer ───────────────────────────────────────────────────────────────

export function SettingsDrawer() {
  const {
    token,
    modelId,
    relayUrl,
    theme,
    effects,
    renderScale,
    settingsOpen,
    connectionState,
    setToken,
    setRelayUrl,
    setTheme,
    setEffect,
    setRenderScale,
    setSettingsOpen,
    generateAndSetToken,
  } = useStore();

  const [activeTab, setActiveTab] = useState<'general' | 'effects'>('general');

  // ── Autostart (desktop only) ──────────────────────────────────────
  const [autostart, setAutostart] = useState<boolean | null>(null);
  const isTauri = typeof window !== 'undefined' && '__TAURI__' in window;

  useEffect(() => {
    if (!isTauri) return;
    import('@tauri-apps/plugin-autostart').then(({ isEnabled }) => {
      isEnabled().then(setAutostart).catch(() => {});
    });
  }, [isTauri]);

  const handleToggleAutostart = async () => {
    if (!isTauri) return;
    const mod = await import('@tauri-apps/plugin-autostart');
    const next = !autostart;
    try {
      if (next) await mod.enable();
      else await mod.disable();
      setAutostart(next);
    } catch (err) {
      console.warn('[Settings] Autostart toggle failed:', err);
    }
  };

  const { sendSetModel } = useWsClient();

  const [tokenInput, setTokenInput] = useState(token ?? '');
  const [relayInput, setRelayInput] = useState(relayUrl);
  const [tokenError, setTokenError] = useState('');

  const isConnected = connectionState === 'connected';

  if (!settingsOpen) return null;

  const handleSaveToken = () => {
    const trimmed = tokenInput.trim();
    if (!trimmed) {
      setToken(null);
      setSettingsOpen(false);
      return;
    }
    if (!isValidToken(trimmed)) {
      setTokenError('Invalid token format');
      return;
    }
    setTokenError('');
    setToken(trimmed);
  };

  const handleSaveRelay = () => {
    setRelayUrl(relayInput.trim() || 'https://relay.projectavatar.io');
  };

  const handleGenerate = () => {
    const t = generateAndSetToken();
    setTokenInput(t);
    setTokenError('');
  };

  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value || null;
    if (!isConnected) return; // Silently ignore if not connected
    sendSetModel(id);
    // Store updates when model_changed echo arrives from DO
  };

  return (
    <div style={overlayStyle} onClick={() => setSettingsOpen(false)}>
      <div style={drawerStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <span style={titleStyle}>Settings</span>
          <button style={closeBtnStyle} onClick={() => setSettingsOpen(false)}>×</button>
        </div>

        {/* Tab bar */}
        <div style={tabBarStyle}>
          <button style={tabStyle(activeTab === 'general')} onClick={() => setActiveTab('general')}>
            General
          </button>
          <button style={tabStyle(activeTab === 'effects')} onClick={() => setActiveTab('effects')}>
            ✨ Effects
          </button>
        </div>

        {activeTab === 'effects' && (
          <>
            <div style={sectionStyle}>
              <div style={{ ...labelStyle, marginBottom: '0.25rem' }}>VISUAL EFFECTS</div>
              <div style={hintStyle}>
                Toggle visual effects on the avatar. Some effects work best together (e.g. Eye Glow + Bloom).
              </div>
            </div>
            <div style={effectToggleStyle}>
              <div>
                <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--color-text)' }}>
                  Render Scale
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginTop: 2 }}>
                  Higher = sharper but heavier ({renderScale}x)
                </div>
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                {[1, 2, 3].map((s) => (
                  <button
                    key={s}
                    onClick={() => setRenderScale(s)}
                    style={{
                      padding: '3px 8px',
                      borderRadius: 4,
                      border: `1px solid ${renderScale === s ? 'var(--color-accent)' : 'var(--color-border)'}`,
                      background: renderScale === s ? 'rgba(108, 92, 231, 0.2)' : 'transparent',
                      color: renderScale === s ? 'var(--color-accent)' : 'var(--color-text-muted)',
                      cursor: 'pointer',
                      fontSize: 11,
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {s}x
                  </button>
                ))}
              </div>
            </div>

            {(Object.keys(EFFECT_LABELS) as (keyof EffectsState)[]).map((effect) => (
              <div key={effect} style={effectToggleStyle}>
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--color-text)' }}>
                    {EFFECT_LABELS[effect]}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginTop: 2 }}>
                    {EFFECT_DESCRIPTIONS[effect]}
                  </div>
                </div>
                <div
                  style={effectToggleSwitchStyle(effects[effect])}
                  onClick={() => setEffect(effect, !effects[effect])}
                  role="switch"
                  aria-checked={effects[effect]}
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setEffect(effect, !effects[effect]); }}
                >
                  <div style={effectToggleKnobStyle(effects[effect])} />
                </div>
              </div>
            ))}
          </>
        )}

        {activeTab === 'general' && <>
        {/* Model picker */}
        <div style={sectionStyle}>
          <label style={labelStyle}>Avatar Model</label>
          <select
            value={modelId ?? ''}
            onChange={handleModelChange}
            style={{ ...selectStyle, opacity: isConnected ? 1 : 0.5 }}
            disabled={!isConnected}
          >
            <option value="" disabled>Select a model...</option>
            {models.map((m: ModelEntry) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
          {!isConnected && (
            <div style={{ ...hintStyle, color: 'var(--color-warning)' }}>
              Connect to the relay to change the model.
            </div>
          )}
          {isConnected && (
            <div style={hintStyle}>
              Model change syncs to all connected screens instantly.
            </div>
          )}
        </div>

        {/* Autostart — desktop only */}
        {isTauri && autostart !== null && (
          <div style={sectionStyle}>
            <div style={effectToggleStyle}>
              <span>Start with OS</span>
              <div
                style={effectToggleSwitchStyle(autostart)}
                onClick={handleToggleAutostart}
              >
                <div style={effectToggleKnobStyle(autostart)} />
              </div>
            </div>
          </div>
        )}

        <div style={{ borderTop: '1px solid var(--color-border)' }} />

        {/* Token */}
        <div style={sectionStyle}>
          <label style={labelStyle}>Token</label>
          <input
            type="text"
            value={tokenInput}
            onChange={(e) => { setTokenInput(e.target.value); setTokenError(''); }}
            placeholder="Enter or generate a token"
            style={inputStyle}
          />
          {tokenError && (
            <span style={{ color: 'var(--color-danger)', fontSize: '0.8rem' }}>{tokenError}</span>
          )}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              style={{ ...btnStyle, background: 'var(--color-accent)', color: '#fff', flex: 1 }}
              onClick={handleSaveToken}
            >
              Save
            </button>
            <button
              style={{
                ...btnStyle,
                background: 'transparent',
                border: '1px solid var(--color-border)',
                color: 'var(--color-text)',
                flex: 1,
              }}
              onClick={handleGenerate}
            >
              New token
            </button>
          </div>
        </div>

        {/* Relay URL */}
        <div style={sectionStyle}>
          <label style={labelStyle}>Relay URL</label>
          <input
            type="text"
            value={relayInput}
            onChange={(e) => setRelayInput(e.target.value)}
            placeholder="https://relay.projectavatar.io"
            style={inputStyle}
          />
          <button
            style={{ ...btnStyle, background: 'var(--color-accent)', color: '#fff' }}
            onClick={handleSaveRelay}
          >
            Update
          </button>
        </div>

        {/* Theme */}
        <div style={sectionStyle}>
          <label style={labelStyle}>Theme</label>
          <select
            value={theme}
            onChange={(e) => setTheme(e.target.value as 'dark' | 'transparent')}
            style={selectStyle}
          >
            <option value="dark">Dark Background</option>
            <option value="transparent">Transparent (OBS)</option>
          </select>
        </div>

        </>}

        <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid var(--color-border)' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
            Project Avatar v1.1.0
            <br />
            Relay: {relayUrl}
          </p>
        </div>
      </div>
    </div>
  );
}
